<template>
  <router-view />

  <!-- Global Business Registration Modal (Dashboard only) -->
  <div v-if="!isWidgetRoute && coreState.isShopRegistrationOpen"
    class="fixed inset-0 z-[100] flex items-center justify-center p-4 bg-black/60 backdrop-blur-sm">
    <div
      class="bg-white dark:bg-slate-900 w-full max-w-md rounded-2xl p-6 shadow-2xl relative overflow-visible h-full md:h-auto">
      <Button @click="coreState.isShopRegistrationOpen = false" variant="ghost"
        class="absolute top-4 right-4 !p-2 text-gray-400 hover:text-gray-900 dark:hover:text-white z-10 !rounded-full">
        <X :size="20" />
      </Button>

      <BusinessRegistrationWizard :user-id="coreState.activeUser?.id" :show-back="false" :force-new="true"
        @complete="handleRegistrationComplete" />
    </div>
  </div>

  <template v-if="!isWidgetRoute">
    <Toast />
    <GlobalInvitationHandler />
    <GlobalMassInvitationHandler />
    <GlobalPartnershipHandler />
    <PWAUpdateBanner />
  </template>
</template>

<script setup>
import { coreState } from './stores/core.js'
import { useShopDashboard } from './stores/useShopDashboard.js'
import { useAuth } from './stores/useAuth.js' // [NEW]
import { useToast } from './composables/useToast.js'
import { computed, watch, onMounted, onUnmounted } from 'vue'
import { X } from 'lucide-vue-next'
import Toast from './components/shared/Toast.vue'
import PWAUpdateBanner from './components/shared/PWAUpdateBanner.vue'
import GlobalInvitationHandler from './components/GlobalInvitationHandler.vue'
import GlobalMassInvitationHandler from './components/GlobalMassInvitationHandler.vue'
import GlobalPartnershipHandler from './components/GlobalPartnershipHandler.vue'
import Button from './components/shared/Button.vue'
import BusinessRegistrationWizard from './components/shop/BusinessRegistrationWizard.vue'
import { supabase } from './supabase'

import { useRouter, useRoute } from 'vue-router'

const dashboard = useShopDashboard();
const auth = useAuth(); // [NEW] Access Auth for verifySession
const toast = useToast();
const router = useRouter();
const route = useRoute();

// Widget routes don't need PWA updates, network monitoring, or session checks
const isWidgetRoute = computed(() => {
  return route.name === 'widget' || route.name === 'map-embed';
});

const handleRegistrationComplete = async (newShop) => {
  coreState.isShopRegistrationOpen = false;
  if (newShop && newShop.id) {
    await dashboard.switchShop(newShop.id);
    toast.showToast("Shop created successfully!");
    router.push({ name: 'map-setup', params: { shopId: newShop.id } });
  }
};

const updateTheme = (isDark) => {
  if (isDark) {
    document.documentElement.classList.add('dark')
  } else {
    document.documentElement.classList.remove('dark')
  }

  // Update status bar color for PWA
  let metaThemeColor = document.querySelector('meta[name="theme-color"]');
  if (!metaThemeColor) {
    metaThemeColor = document.createElement('meta');
    metaThemeColor.setAttribute('name', 'theme-color');
    document.head.appendChild(metaThemeColor);
  }
  // Setting to Slate 950 for dark and pure white for light
  metaThemeColor.setAttribute('content', isDark ? '#020617' : '#ffffff');
}

watch(() => coreState.darkMode, (val) => {
  updateTheme(val)
  try {
    localStorage.setItem('lewotobi-theme', val ? 'dark' : 'light')
  } catch (e) {
    console.warn('Could not save theme to localStorage', e)
  }
})

// Global Auth Watcher: Redirect if user is logged out while on protected route
watch(() => coreState.activeUser, (user) => {
  if (!user && route.meta.requiresAuth !== false && route.name !== 'start' && route.name !== 'map-embed' && route.name !== 'widget') {
    // Double check we aren't already there to avoid duplicate nav errors
    if (router.currentRoute.value.name !== 'start') {
      router.push({ name: 'start' });
    }
  }
})

const checkSession = async () => {
  // [SAFE WAKE] Use shared verifySession action
  // This ensures "Safe Wake" protocol (Timeout + Recovery + Feedback) runs on focus too
  await auth.verifySession();
}

let realtimeSubscription = null;

onMounted(async () => {
  updateTheme(coreState.darkMode)

  // Skip all dashboard-level services for widget/embed routes
  if (isWidgetRoute.value) return;

  window.addEventListener('focus', checkSession)

  // [REALTIME CANARY] Monitor global connection state
  const { networkUtils } = await import('./utils/networkUtils.js');

  let wasDisconnected = false;

  realtimeSubscription = await networkUtils.monitorRealtimeConnection((status) => {
    if (status === 'SUBSCRIBED') {
      console.log("[App] Realtime Connected. Verifying system health...");

      // Trigger sync with forced profile hydration
      auth.verifySession(true);
      dashboard.syncLogs();

      // Only toast if we were actively disconnected before
      if (wasDisconnected) {
        toast.showToast("Connection restored", "success");
        wasDisconnected = false;
      }

    } else if (status === 'CLOSED' || status === 'CHANNEL_ERROR' || status === 'TIMED_OUT') {
      console.warn(`[App] Realtime Disconnected (${status}).`);

      // Only toast once per disconnect episode
      if (!wasDisconnected) {
        toast.showToast("Connection lost. Reconnecting...", "warning");
        wasDisconnected = true;
      }
    }
  });

  // Initialize PWA Sync (Legacy/Redundant but good backup)
  window.addEventListener('online', async () => {
    console.log("App reported online (Window Event). Verifying...");
    if (await networkUtils.checkConnectivity()) {
      dashboard.syncLogs();
    }
  });

  // [RETRY GATE 1] Visibility change listener
  // OS often restores dormant network states when app is pulled from background
  document.addEventListener('visibilitychange', async () => {
    if (document.visibilityState === 'visible') {
      console.log("[App] App foregrounded. Verifying connection for sync...");
      // Check connectivity (uses _sw_bypass ping)
      if (await networkUtils.checkConnectivity()) {
        dashboard.syncLogs();
      }
    }
  });

  // Initial check
  if (navigator.onLine) {
    (async () => {
      if (await networkUtils.checkConnectivity()) {
        dashboard.syncLogs();
      }
    })();
  }
})

onUnmounted(() => {
  window.removeEventListener('focus', checkSession)
  if (realtimeSubscription) realtimeSubscription.unsubscribe();
  document.removeEventListener('visibilitychange', () => { }); // Basic cleanup 
})
</script>
